import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

export abstract class HubService {
  public readonly hubConnection: HubConnection;
  private startPromise: Promise<void> | null = null;

  constructor(url: string) {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(url)
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect()
      .build();
  }

  /**
   * Ensures the SignalR connection is ready before invoking server methods.
   * Handles all connection states appropriately.
   * @throws Error if connection cannot be established
   */
  public async ensureConnected(): Promise<void> {
    const state = this.hubConnection.state;

    if (state === HubConnectionState.Connected) {
      return;
    }

    if (state === HubConnectionState.Disconnected) {
      await this.startConnection();
      return;
    }

    // Connecting or Reconnecting — handled internally by SignalR's
    // withAutomaticReconnect(). Nothing for us to do here; the caller
    // (RouteSignalRGroupService) will be notified via onreconnected/onclose.
    throw new Error(`Cannot connect - connection is in ${state} state`);
  }

  private async startConnection(): Promise<void> {
    if (!this.startPromise) {
      this.startPromise = this.hubConnection.start().finally(() => {
        this.startPromise = null;
      });
    }

    await this.startPromise;
  }
}
